import servicesConfig, { CustomAxiosHeaders } from '../config/services'
import { requests } from '.'
import { AxiosResponse } from 'axios'

export interface IContactByBusinessByIdPayload {
  locationId: string
  businessId: string,
  limit: number
  skip: number,
  query?: string
}

export interface IAddBusinessToContacts {
  ids: string[],
  locationId: string,
  businessId: string | null
}

export interface IFetchContactsWithoutBusinessId {
  locationId: string
  businessId: string
  query?: string
  size?: string
  skip?: string
}

export interface IFetchContactsByIds {
  locationId: string
  ids: string[]
}

// Configure request setup
const serviceName = 'ContactService'
const host = servicesConfig.Contacts
const opts: CustomAxiosHeaders = { host, serviceName }
const request = (addlOpts?: CustomAxiosHeaders) => {
  return requests(Object.assign({}, opts, addlOpts))
}

// Helper function to handle response
const responseBody = (res: AxiosResponse) => res.data

// Define service object with methods
const ContactService = {
  getDeleted: (locationId: string, startAt?: string) =>
    request().get(
      `/${locationId}/deleted/${startAt ? '?startAt=' + startAt : ''}`
    ).then(responseBody),

  deleteContacts: (contacts: string[]) =>
    request().post(`/bulk/delete/`, { contacts }).then(responseBody),

  restoreContacts: (contacts: string[], locationId: string) =>
    request().post(`/bulk/restore/`, { contacts, locationId }).then(responseBody),

  update: (id: string, data: { [key: string]: any }) =>
    request().put(`/${id}?version=14-05-22`, data).then(responseBody),

  create: (data: { [key: string]: any }) => 
    request().post(`/?version=14-05-22`, data).then(responseBody),

  getAudit: (id: string) => 
    request().get(`/${id}/audits`).then(responseBody),

  read: (id: string) => 
    request().get(`/${id}`).then(responseBody),

  searchDuplicateByPhone: (locationId: string, number: string) => 
    request().get(`/search/duplicate?locationId=${locationId}&number=${encodeURIComponent(number)}`).then(responseBody),

  validateEmail: (id: string) => 
    request().post(`/${id}/validateEmail/`, {}).then(responseBody),

  syncOAuth: (id: string) => 
    request().post(`/${id}/syncOAuth/`, {}).then(responseBody),

  getByLocationId: (id: string) => 
    request().get(`/?locationId=${id}`).then(responseBody),

  Notes: {
    create: (contactId: string, data: { [key: string]: any }) =>
      request().post(`/${contactId}/notes/`, data).then(responseBody),

    read: (contactId: string, noteId: string) =>
      request().get(`/${contactId}/notes/${noteId}`).then(responseBody),

    update: (contactId: string, noteId: string, data: { [key: string]: any }) =>
      request().put(`/${contactId}/notes/${noteId}`, data).then(responseBody),

    delete: (contactId: string, noteId: string) =>
      request().delete(`/${contactId}/notes/${noteId}`).then(responseBody),

    list: (contactId: string) => 
      request().get(`/${contactId}/notes/`).then(responseBody),

    restore: (noteId: string, contactId: string, forceRestore: boolean) =>
      request().put(`/${contactId}/notes/${noteId}/restore`, { forceRestore: forceRestore }).then(responseBody),
  },

  Tasks: {
    create: (contactId: string, data: { [key: string]: any }) =>
      request().post(`/${contactId}/tasks/`, data).then(responseBody),

    read: (contactId: string, taskId: string) =>
      request().get(`/${contactId}/tasks/${taskId}`).then(responseBody),

    update: (contactId: string, taskId: string, data: { [key: string]: any }) =>
      request().put(`/${contactId}/tasks/${taskId}`, data).then(responseBody),

    delete: (contactId: string, taskId: string) =>
      request().delete(`/${contactId}/tasks/${taskId}`).then(responseBody),

    list: (contactId: string) => 
      request().get(`/${contactId}/tasks/`).then(responseBody),

    restore: (taskId: string, contactId: string, forceRestore: boolean) =>
      request().put(`/${contactId}/tasks/${taskId}/restore`, { forceRestore: forceRestore }).then(responseBody),
  },

  SmartList: {
    create: (data: { [key: string]: any }) =>
      request().post(`/smartlist/`, data).then(responseBody),

    read: (id: string) =>
      request().get(`/smartlist/${id}`).then(responseBody),

    delete: (smartListId: string) =>
      request().delete(`/smartlist/${smartListId}`).then(responseBody),

    update: (smartListId: string, data: { [key: string]: any }) =>
      request().put(`/smartlist/${smartListId}`, data).then(responseBody),

    list: (locationId: string, includeGlobals: boolean = false, userId?: string) =>
      request().get(`/smartlist/search?locationId=${locationId}${userId ? '&userId=' + userId : ''}${includeGlobals ? '&globals=' + includeGlobals : ''}`).then(responseBody),

    searchContacts: (data: any) =>
      request().post(`/search`, data).then(responseBody),

    searchContactsV2: (data: any) =>
      request().post(`/search/2`, data).then(responseBody),

    searchContactsByIds: (data: IFetchContactsByIds) =>
      request().post(`/search/ids/es`, data).then(responseBody),
  },

  Businesses: {
    searchContactsByBusinessId: (payload: IContactByBusinessByIdPayload) =>
      request().get(
        `/business/${payload.businessId}`,
        {
          params: {
            locationId: payload.locationId,
            limit: payload.limit,
            skip: payload.skip,
            count: true,
            query: payload.query,
          },
        }
      ).then(responseBody),

    fetchContactsWithoutBusinessId: (payload: IFetchContactsWithoutBusinessId) =>
      request().get(
        `/search/business`,
        {
          params: {
            locationId: payload.locationId,
            businessId: payload.businessId,
            query: payload.query,
            size: payload.size,
            skip: payload.skip
          },
        }
      ).then(responseBody)
  },

  Bulk: {
    addBusinessIdToContacts: (payload: IAddBusinessToContacts) =>
      request().post(
        `/bulk/business`,
        {
          ...payload
        }
      ).then(responseBody)
  },

  deleteSmartlistContacts: (
    contacts: string[],
    smartList: any,
    locationId: string
  ) =>
    request().post(`/bulk/smartlist/delete/`, {
      contacts,
      smartList,
      locationId,
    }).then(responseBody),

  addTag: (contactId: string, tags: string[]) => 
    request().post(`/${contactId}/tags`, { tags }).then(responseBody),

  removeTag: (contactId: string, tags: string[]) => 
    request().delete(`/${contactId}/tags`, { data: { tags } }).then(responseBody),

  addTags: (tags: string[], note: string, contacts: string[], smartList: any, locationId: string) =>
    request().post(`/bulk/tags/add`, { contacts, smartList, locationId, note, tags }).then(responseBody),

  removeTags: (tags: string[], note: string, contacts: string[], smartList: any, locationId: string) =>
    request().post(`/bulk/tags/remove`, { contacts, smartList, locationId, note, tags }).then(responseBody),

  getImportLog: (importId: string, skip: number, limit: number) => 
    request().get(`/bulk/import/${importId}/logs?skip=${skip}&limit=${limit}`).then(responseBody),

  revertImport: (importId: string) => 
    request().get(`/bulk/import/${importId}/revert`).then(responseBody),

  restoreBulkdelete: (bulkRequestId: string) => 
    request().get(`/bulkRequests/${bulkRequestId}/restore`).then(responseBody),

  cancelImport: (importId: string) => 
    request().post(`/bulk/import/${importId}/status`, { "status": "cancelled" }).then(responseBody),

  import: (data: { [key: string]: any }) => 
    request().post(`/bulk/import/`, data).then(responseBody),

  merge: (data: { [key: string]: any }) => 
    request().post(`/merge/`, data).then(responseBody),

  restoreContact: (id: string) => 
    request().put(`/${id}/restore`, {}).then(responseBody),

  getContactDetails: (data: {
    allDetails: boolean,
    locationId: string,
    contactIds: string[]
  }) => request().post('/search/ids/es', { locationId: data.locationId, ids: data.contactIds }).then(responseBody)
}

export default ContactService
